import { useCallback, useMemo } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';

import GetLinkableLocation from 'features/organisation/views/organisation-details/components/electronic-client-record/components/link-room-modal/domain/models/get-linkable-location';

export type FlattenedLocation = GetLinkableLocation & {
  level: number;
  hasChildren: boolean;
  isVisible: boolean;
  parentId?: string;
};

export type UseVirtualizedTreeHook = {
  flattenedLocations: FlattenedLocation[];
  expandedItems: Set<string>;
  toggleExpanded: (locationId: string) => void;
  isExpanded: (locationId: string) => boolean;
};

const useVirtualizedTree = (locations: GetLinkableLocation[] | undefined): UseVirtualizedTreeHook => {
  const [expandedIds, setExpandedIds] = useSessionStorage<string[]>(FilterSessionStorageKeys.LINK_ROOM_MODAL_EXPANDED_NODES, []);

  const expandedItems = useMemo(() => new Set(expandedIds), [expandedIds]);

  const toggleExpanded = useCallback(
    (locationId: string) => {
      setExpandedIds((prev) => {
        if (prev.includes(locationId)) {
          return prev.filter((id) => id !== locationId);
        }
        return [...prev, locationId];
      });
    },
    [setExpandedIds],
  );

  const isExpanded = useCallback(
    (locationId: string) => {
      return expandedItems.has(locationId);
    },
    [expandedItems],
  );

  const flattenedLocations = useMemo(() => {
    if (!locations) return [];

    const flatten = (items: GetLinkableLocation[], level: number = 0, parentId?: string): FlattenedLocation[] => {
      const result: FlattenedLocation[] = [];

      for (const item of items) {
        const hasChildren = Boolean(item.children && item.children.length > 0);
        const isExpanded = expandedItems.has(item.id);

        result.push({
          ...item,
          level,
          hasChildren,
          isVisible: true,
          parentId,
        });

        if (hasChildren && isExpanded && item.children) {
          result.push(...flatten(item.children, level + 1, item.id));
        }
      }

      return result;
    };

    return flatten(locations);
  }, [locations, expandedItems]);

  return {
    flattenedLocations,
    expandedItems,
    toggleExpanded,
    isExpanded,
  };
};

export default useVirtualizedTree;
